import React, { useState } from 'react';
import { Camera, Lock, Sparkles, ArrowRight } from 'lucide-react';

/**
 * Before/After gallery placeholder. No real images yet — surfaces a
 * privacy-respecting "available on consultation" experience that
 * directs patients to the Request Appointment flow.
 */
const BeforeAfterGallery = ({ treatmentName = 'this treatment', accent = { from: '#1E5BA8', to: '#1FA7A1' } }) => {
  const [activePair, setActivePair] = useState(0);

  // Placeholder data — three blurred pairs hinting at the gallery
  const pairs = [0, 1, 2];

  return (
    <section className="py-16" style={{ background: '#F8FAFC' }} data-testid="before-after-gallery">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: accent.from }}>
            Real Patient Outcomes
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            Before &amp; After
          </h2>
          <p className="text-slate-500 mt-2 text-sm max-w-2xl mx-auto leading-relaxed">
            Out of respect for patient privacy, full results are shared confidentially during your consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Before */}
          <div
            className="relative rounded-[24px] overflow-hidden border border-slate-100 aspect-[4/5]"
            style={{ background: `linear-gradient(135deg, ${accent.from}30, ${accent.to}40)` }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <Camera className="h-10 w-10 text-white/80 mb-3" />
              <p className="text-white font-extrabold text-2xl">Before</p>
              <p className="text-white/80 text-sm mt-1">Real patient · Pair {activePair + 1}</p>
            </div>
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur text-[10px] font-bold text-slate-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
              <Lock className="h-3 w-3" /> Confidential
            </span>
          </div>

          {/* After */}
          <div
            className="relative rounded-[24px] overflow-hidden border border-slate-100 aspect-[4/5]"
            style={{ background: `linear-gradient(135deg, ${accent.to}, ${accent.from})` }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <Sparkles className="h-10 w-10 text-white/90 mb-3" />
              <p className="text-white font-extrabold text-2xl">After</p>
              <p className="text-white/85 text-sm mt-1">Available on consultation</p>
            </div>
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur text-[10px] font-bold text-slate-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
              ✓ Verified result
            </span>
          </div>
        </div>

        {/* Pair selector */}
        <div className="flex justify-center gap-2 mt-6" role="tablist" aria-label="Before/after pairs">
          {pairs.map((p) => (
            <button
              key={p}
              onClick={() => setActivePair(p)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${activePair === p ? 'w-8' : 'bg-slate-300 hover:bg-slate-400'}`}
              style={activePair === p ? { background: accent.from } : {}}
              aria-label={`Pair ${p + 1}`}
              data-testid={`before-after-pair-${p}`}
            />
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href="#request-form"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#1FA7A1] hover:text-[#1FA7A1] text-slate-800 font-semibold rounded-full px-6 py-3 transition-all"
            data-testid="before-after-request-cta"
          >
            Request {treatmentName} consultation to view full gallery
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterGallery;
